import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { useFunnel } from '../../contexts/FunnelContext';
import { useCart } from '../../contexts/CartContext';
import { addonQualifies, upgradeTarget } from '../../utils/pricing';
import { track } from '../../utils/analytics';
import type { BundleId } from '../../data/pricing';
import { UpgradeOffer } from './UpgradeOffer';
import { ExpansionOffer } from './ExpansionOffer';
import { BundleSuggestOffer } from './BundleSuggestOffer';
export function PurchaseFlowModal() {
  const {
    state,
    setState,
    close
  } = useFunnel();
  const {
    addSystem,
    addIndividual
  } = useCart();
  const navigate = useNavigate();
  useEffect(() => {
    if (state?.step === 'done') {
      setState(null);
      navigate('/cart');
    }
  }, [state, navigate, setState]);
  useEffect(() => {
    if (!state || state.step === 'done') return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [state, close]);
  const goExpandOrFinish = (bundleId: BundleId) => {
    if (addonQualifies(bundleId)) {
      setState({
        step: 'expand',
        bundleId,
        stepIndex: 2,
        totalSteps: 2
      });
    } else {
      addSystem(bundleId, 0);
      setState({
        step: 'done'
      });
    }
  };
  const open = state !== null && state.step !== 'done';
  return <AnimatePresence data-id="element-814" data-mp="1mzldqgk5ndue">
      {open && state && state.step !== 'done' && <motion.div className="fixed inset-0 z-50 flex items-end justify-center bg-[#030A14]/80 backdrop-blur-sm sm:items-center sm:p-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} onClick={close} data-id="element-815" data-mp="1i0ibf3vk7lq4">
          <motion.div role="dialog" aria-modal="true" aria-labelledby="funnel-title" className="relative max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-2xl border mf-hairline bg-navy p-6 shadow-2xl sm:rounded-2xl md:p-10" initial={{
        opacity: 0,
        scale: 0.96,
        y: 12
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 12
      }} transition={{
        duration: 0.24,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={e => e.stopPropagation()} data-id="element-816" data-mp="1npil9qbu9f3h">
            <button type="button" onClick={close} aria-label="Close and return without adding" className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-md text-titanium hover:bg-white/5 hover:text-white" data-id="element-817" data-mp="2gdeak9z2w4q8">
              <XIcon className="h-5 w-5" data-id="element-818" data-mp="2uce1joa2cvpk" />
            </button>
            <p className="text-xs font-medium uppercase tracking-[0.14em] text-titanium/70" data-id="element-819" data-mp="2uat9q6f4vqve">
              Optional · step {state.stepIndex} of {state.totalSteps}
            </p>

            {state.step === 'upgrade' && <UpgradeOffer bundleId={state.bundleId} onAccept={() => {
          const target = upgradeTarget(state.bundleId);
          track('upgrade_offer', {
            action: 'accept',
            from: state.bundleId,
            to: target?.id
          });
          goExpandOrFinish(target ? target.id : state.bundleId);
        }} onDecline={() => {
          track('upgrade_offer', {
            action: 'decline',
            from: state.bundleId
          });
          goExpandOrFinish(state.bundleId);
        }} data-id="element-820" data-mp="2uieoqa8vavhr" />}

            {state.step === 'expand' && <ExpansionOffer bundleId={state.bundleId} onAccept={extras => {
          track('expansion_offer', {
            action: 'accept',
            bundleId: state.bundleId,
            extras
          });
          addSystem(state.bundleId, extras);
          setState({
            step: 'done'
          });
        }} onDecline={() => {
          track('expansion_offer', {
            action: 'decline',
            bundleId: state.bundleId
          });
          addSystem(state.bundleId, 0);
          setState({
            step: 'done'
          });
        }} onSwitch={id => {
          track('expansion_offer', {
            action: 'switch_bundle',
            from: state.bundleId,
            to: id
          });
          addSystem(id, 0);
          setState({
            step: 'done'
          });
        }} data-id="element-821" data-mp="9th71chetfhg" />}

            {state.step === 'bundleSuggest' && <BundleSuggestOffer qty={state.qty} onAccept={id => {
          track('bundle_suggest', {
            action: 'accept',
            qty: state.qty,
            to: id
          });
          goExpandOrFinish(id);
        }} onDecline={() => {
          track('bundle_suggest', {
            action: 'decline',
            qty: state.qty
          });
          addIndividual(state.qty);
          setState({
            step: 'done'
          });
        }} data-id="element-822" data-mp="3ip4w3h5igie" />}
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}